import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { sourceMailFallbackLink } from '../../domain/gmail-link';
import { sourceMailBackTarget } from '../../domain/source-navigation';
import { api, ApiError } from '../api';
import type { SourceMailFallback, SourceMailResponse } from '../types';

type SourceMail = SourceMailResponse['message'];

function formatReceivedAt(value: string): string {
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo',
  }).format(new Date(value));
}

export function SourceMailView({ message, onBack }: { message: SourceMail; onBack: () => void }) {
  return (
    <article className="source-mail-view">
      <button className="source-mail-back" type="button" onClick={onBack}>← 戻る</button>
      <header>
        <p className="eyebrow">判定に使った元メール</p>
        <h1>{message.subject}</h1>
        <dl>
          <div><dt>送信元</dt><dd>{message.sender}</dd></div>
          <div><dt>受信日時</dt><dd>{formatReceivedAt(message.receivedAt)}</dd></div>
        </dl>
      </header>
      <pre className="source-mail-body">{message.body}</pre>
      <div className="source-mail-actions">
        <button className="button secondary" type="button" onClick={onBack}>戻る</button>
        <a className="button ghost" href={message.gmailUrl} target="_blank" rel="noreferrer">Gmailでも確認する <span aria-hidden="true">↗</span></a>
      </div>
    </article>
  );
}

export function SourceMailPage() {
  const { messageId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sourceState = location.state as { sourceMailFallback?: SourceMailFallback; sourceReturnTo?: string } | null;
  const fallback = sourceState?.sourceMailFallback;
  const [message, setMessage] = useState<SourceMail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api<SourceMailResponse>(`/api/gmail/messages/${encodeURIComponent(messageId)}/source`);
      setMessage(result.message);
    } catch (caught) {
      const messageText = caught instanceof ApiError && caught.code === 'GOOGLE_REAUTH_REQUIRED'
        ? 'Googleとの再接続が必要です。設定画面から接続し直してください。'
        : caught instanceof Error ? caught.message : '元メールを取得できませんでした。';
      setError(messageText);
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => { void load(); }, [load]);

  const goBack = useCallback(() => {
    const target = sourceMailBackTarget(sourceState?.sourceReturnTo, location.key);
    if (typeof target === 'string') navigate(target, { replace: true });
    else navigate(target);
  }, [location.key, navigate, sourceState?.sourceReturnTo]);

  if (loading) return <div className="source-mail-status" role="status">元メールを開いています…</div>;
  if (error) return <div className="source-mail-status error" role="alert">
    <button className="source-mail-back" type="button" onClick={goBack}>← 戻る</button>
    <strong>元メールを開けませんでした</strong><p>{error}</p>
    {fallback && <div className="source-mail-fallback">
      <h1>{fallback.subject}</h1><p>{fallback.sender}</p><pre>{fallback.sourceText}</pre>
      <a className="button secondary" href={sourceMailFallbackLink(messageId, fallback.subject, fallback.sender, fallback.threadId)}>Gmailで探す</a>
    </div>}
    <div className="source-mail-actions"><button className="button ghost" type="button" onClick={goBack}>戻る</button><button className="button primary" onClick={() => void load()}>もう一度試す</button></div>
  </div>;
  return message ? <SourceMailView message={message} onBack={goBack} /> : null;
}
